import { Avatar, AvatarFallback, AvatarImage } from '@/components/avatar'
import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import {
  DataTable,
  type DataTableColumnDef
} from '@frontend/components/enhanced/data-table'
import { FormattedTeamPerson } from '@frontend/core'
import { IMAGE_PRESETS } from '@frontend/core/lib/modules/images'
import { z } from 'zod'
import { StaticAssetImage } from '../../modules/images/components'
import { requiredResolver } from '../../utils/form-resolver'
import { TeamPeopleData } from './hooks'

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/form'
import { Input } from '@/components/input'
import {
  useTeampeople_createMutation,
  useTeampeople_updateMutation
} from '@frontend/core/lib/domains/team_people'
import { usePeopleData, useTeamsData } from '@frontend/react'
import { SearchableDropdown, toast } from '@frontend/ui'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

export function TeamPersonTable({
  formattedFilteredData,
  isLoading,
  filters,
  updateFilterValue,
  showFilters,
  teamId,
  personId
}: TeamPeopleData & {
  teamId?: string
  personId?: string
  showFilters?: (keyof TeamPeopleData['filters'])[]
}) {
  const navigate = useNavigate()

  const teams = useTeamsData({})

  const columns: DataTableColumnDef<FormattedTeamPerson>[] = [
    {
      id: 'number',
      header: '#',
      accessorFn: (row) => row.number ?? 0,
      cell: ({ row }) => {
        return <div className='font-medium'>{row.original.number || '-'}</div>
      }
    },
    {
      id: 'name',
      accessorFn: (row) => row.fullName,
      header: 'Name',
      cell: ({ row }) => {
        const teamPerson = row.original
        if (teamId) {
          return (
            <div className='flex items-center gap-3'>
              <Avatar className='h-8 w-8'>
                <StaticAssetImage
                  src={teamPerson.img}
                  alt={teamPerson?.firstName || 'Player'}
                  preset={IMAGE_PRESETS.playerThumbnail}
                  fallback={
                    <AvatarFallback>
                      {teamPerson?.firstName?.[0] || ''}
                      {teamPerson?.lastName?.[0] || ''}
                    </AvatarFallback>
                  }
                />
              </Avatar>
              <div>
                <Button
                  variant='link'
                  className='text-foreground w-fit px-0 text-left'
                  onClick={() => navigate(`/people/${teamPerson.personId}`)}
                >
                  {teamPerson?.firstName} {teamPerson?.lastName}
                </Button>
                {teamPerson.startDate && (
                  <div className='text-muted-foreground text-xs'>
                    Joined {new Date(teamPerson.startDate).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          )
        } else {
          const team = teams.map[teamPerson.teamId]

          if (!team) return <div>Unknown Team</div>

          return (
            <div className='flex items-center gap-3'>
              <Avatar className='h-8 w-8'>
                {team?.logo ? (
                  <AvatarImage src={team.logo} alt={team?.name || 'Team'} />
                ) : null}
                <AvatarFallback>{team?.name?.[0] || ''}</AvatarFallback>
              </Avatar>
              <div>
                <Button
                  variant='link'
                  className='text-foreground w-fit px-0 text-left'
                  onClick={() => navigate(`/teams/${team.id}`)}
                >
                  {team?.name}
                </Button>
              </div>
            </div>
          )
        }
      }
    },
    {
      id: 'position',
      header: 'Position',
      accessorFn: (row) => row.person.defaultPosition ?? '',
      cell: ({ row }) => <div>{row.original.person.defaultPosition || '-'}</div>
    },
    {
      id: 'startDate',
      header: 'Start Date',
      accessorFn: (row) => row.startDate ?? '',
      cell: ({ row }) => (
        <div className='text-muted-foreground text-sm'>
          {row.original.startDate
            ? new Date(row.original.startDate).toLocaleDateString()
            : '-'}
        </div>
      )
    },
    {
      id: 'endDate',
      header: 'End Date',
      accessorFn: (row) => row.endDate ?? '',
      cell: ({ row }) => (
        <div className='text-muted-foreground text-sm'>
          {row.original.endDate
            ? new Date(row.original.endDate).toLocaleDateString()
            : '-'}
        </div>
      )
    },
    {
      id: 'status',
      header: () => <div className='w-full text-right'>Status</div>,
      cell: ({ row }) => (
        <div className='w-full text-right'>
          {row.original.active ? (
            <Badge
              variant='outline'
              className='ml-auto border-emerald-200 bg-emerald-50 text-emerald-500'
            >
              Active
            </Badge>
          ) : (
            <Badge
              variant='outline'
              className='ml-auto border-amber-200 bg-amber-50 text-amber-500'
            >
              Inactive
            </Badge>
          )}
        </div>
      )
    }
  ]

  return (
    <DataTable
      name={teamId ? 'Team Member' : 'To Team'}
      columns={columns}
      data={formattedFilteredData}
      dataForm={(props: {
        onSubmit: (values: z.infer<typeof formSchema>) => void
      }) => <TeamPersonForm {...props} teamId={teamId} personId={personId} />}
      isLoading={isLoading}
      filters={filters}
      updateFilterValue={updateFilterValue}
      showFilters={showFilters ?? []}
      defaultSorting={[{ id: 'name', desc: false }]}
    />
  )
}

const formSchema = z.object({
  id: z.string().optional(),
  teamId: z.string().min(1, 'Please select a team'),
  personId: z.string().min(1, 'Please select a player'),
  number: z.coerce.string().optional(),
  startDate: z
    .string()
    .nullish()
    .transform((v) => v ?? ''),
  endDate: z
    .string()
    .nullish()
    .transform((v) => v ?? '')
})

type FormValues = z.infer<typeof formSchema>

interface TeamPersonFormProps {
  onSubmit: (values: FormValues) => void
  initialValues?: z.input<typeof formSchema>
  teamId?: string
  personId?: string
}

export function TeamPersonForm({
  onSubmit,
  initialValues,
  teamId,
  personId
}: TeamPersonFormProps) {
  // Fetching teams and people data //

  useEffect(() => {
    console.log('TeamPersonForm mounted')
  }, [])

  const {
    filteredData: peopleData,
    map: peopleMap,
    isLoading: isPeopleDataLoading
  } = usePeopleData({})
  const {
    filteredData: teamsData,
    map: teamsMap,
    isLoading: isTeamDataLoading
  } = useTeamsData({})

  const teamsOptions = useMemo(() => {
    if (isTeamDataLoading) return []
    return teamsData.map((team) => ({
      value: team.id,
      name: team.name
    }))
  }, [isTeamDataLoading, teamsData])

  const peopleOptions = useMemo(() => {
    if (isPeopleDataLoading) return []
    return peopleData.map((person) => ({
      value: person.id,
      name: `${person.firstName} ${person.lastName}`
    }))
  }, [isPeopleDataLoading, peopleData])

  const [updateTeamPerson, updateResult] = useTeampeople_updateMutation()
  const [createTeamPerson, createResult] = useTeampeople_createMutation()

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      if (values.id) {
        toast.promise(
          updateTeamPerson({
            id: values.id,
            teamId: values.teamId,
            personId: values.personId,
            number: parseInt(values.number ?? ''),
            startDate: values.startDate || null,
            endDate: values.endDate || null
          }).unwrap(),
          {
            loading: 'Updating...',
            success: () => {
              const person = peopleMap[values.personId]
              onSubmit(values)
              return `${person.firstName} ${person.lastName} has been updated`
            },
            error: (error) => {
              return 'Error updating team person: ' + error.message
            }
          }
        )
      } else {
        toast.promise(
          createTeamPerson({
            teamId: values.teamId,
            personId: values.personId,
            number: parseInt(values.number ?? ''),
            startDate: values.startDate || null,
            endDate: values.endDate || null
          }).unwrap(),
          {
            loading: 'Creating...',
            success: () => {
              const person = peopleMap[values.personId]
              const team = teamsMap[values.teamId]
              onSubmit(values)
              return `${person.firstName} ${person.lastName} has been added to ${team.name}`
            },
            error: (error) => {
              const person = peopleMap[values.personId]
              const team = teamsMap[values.teamId]
              console.log('Error:', error)
              return `Error adding  ${person.firstName} ${person.lastName} to ${team.name}`
            }
          }
        )
      }
    } catch (err) {
      console.error('Update failed:', err)
    }
  }

  const isEditing = !!initialValues?.id

  const form = useForm<z.input<typeof formSchema>, unknown, FormValues>({
    resolver: requiredResolver(formSchema),
    defaultValues: {
      teamId: initialValues?.teamId || teamId || '',
      personId: initialValues?.personId || personId || '',
      number: initialValues?.number || '',
      startDate: initialValues?.startDate || '',
      endDate: initialValues?.endDate || '',
      ...initialValues
    }
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-8'>
        <div className='flex gap-4'>
          <p className='text-sm'>Team Person ID</p>
          <Badge variant={'secondary'}>{form.getValues('id')}</Badge>
        </div>
        {!teamId && !!teamsOptions && (
          <FormField
            control={form.control}
            name='teamId'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Team</FormLabel>
                {isEditing ? (
                  <div className='text-sm font-medium'>
                    {teamsMap[field.value]?.name || field.value}
                  </div>
                ) : (
                  <SearchableDropdown
                    value={field.value}
                    onSelect={(value) => field.onChange(value.toString())}
                    options={teamsOptions}
                    placeholder='Select team'
                    searchPlaceholder='Search teams'
                    className='min-w-[250px]'
                  />
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {!personId && !!peopleOptions && (
          <FormField
            control={form.control}
            name='personId'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Person</FormLabel>
                <SearchableDropdown
                  value={field.value}
                  onSelect={(value) => field.onChange(value.toString())}
                  options={peopleOptions}
                  placeholder='Select person'
                  searchPlaceholder='Search people'
                  className='min-w-[250px]'
                />
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name='number'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Player Number</FormLabel>
              <FormControl>
                <Input
                  type='number'
                  placeholder='Enter player number'
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='startDate'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Start Date</FormLabel>
              <FormControl>
                <Input
                  type='date'
                  {...field}
                  value={field.value ? field.value.split('T')[0] : ''}
                  onChange={(value) => {
                    field.onChange(value ? new Date(value).toISOString() : '')
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='endDate'
          render={({ field }) => (
            <FormItem>
              <FormLabel>End Date</FormLabel>
              <FormControl>
                <Input
                  type='date'
                  {...field}
                  value={field.value ? field.value.split('T')[0] : ''}
                  onChange={(value) => {
                    field.onChange(value ? new Date(value).toISOString() : '')
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className='flex justify-end'>
          <Button className='' type='submit'>
            {form.getValues().id
              ? updateResult.isLoading
                ? 'Updating...'
                : 'Update'
              : createResult.isLoading
                ? 'Creating...'
                : 'Create'}
          </Button>
        </div>
      </form>
    </Form>
  )
}
